import type { Descriptor, Identity } from './types';

/**
 * The names of the organizations and projects a person may see, by identifier, from what this
 * application has been told: the session's memberships and every family descriptor read so far (the
 * organizations, the current organization's projects and the project in view). A name is shown before
 * an identifier wherever one is known; the identifier stays the fallback, never a guess.
 */
export class Names {
	#known = new Map<string, string>();
	#listeners = new Set<() => void>();
	#asked = new Set<string>();

	/** Learns the names a session or a descriptor carries. @returns Whether anything new was learned */
	learn(source: Identity | Descriptor | { unavailable: true } | null | undefined): boolean {
		if (!source || 'unavailable' in source) return false;
		const before = this.#known.size;
		const entries: { id: string; name?: string }[] =
			'account' in source
				? source.organizations.map(entry => ({ id: entry.organization, name: entry.name }))
				: [...(source.organizations ?? []), ...(source.projects ?? []), ...(source.project ? [source.project] : []), ...(source.organization ? [source.organization] : [])];
		for (const { id, name } of entries) if (name && name !== id) this.#known.set(id, name);
		const learned = this.#known.size !== before;
		if (learned) for (const listener of this.#listeners) listener();
		return learned;
	}

	/** Hears every time a name is learned. @returns What stops listening */
	listen = (listener: () => void): (() => void) => {
		this.#listeners.add(listener);
		return () => void this.#listeners.delete(listener);
	};

	/**
	 * Whether an organization's projects should be asked for: the first time only, so a list of many
	 * rows of one organization asks once
	 */
	wants(organization: string): boolean {
		if (this.#asked.has(organization)) return false;
		this.#asked.add(organization);
		return true;
	}

	/** The name of an organization or project, when known */
	name(id: string | undefined): string | undefined {
		return id ? this.#known.get(id) : undefined;
	}
}
